import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import kioskApi from '../services/api/kiosk/kioskApi';
import paths from '../routes/paths';
import {getWelbeeHomeUrl} from '../utils/kioskAccess';

const KioskContext = createContext();

export const useKiosk = () => {
  const context = useContext(KioskContext);
  if (!context) {
    throw new Error('useKiosk must be used within a KioskProvider');
  }
  return context;
};

const KIOSK_SESSION_KEY = 'welbee-kiosk-session';

export const KioskProvider = ({ children }) => {
  const { schoolSlug } = useParams();
  const navigate = useNavigate();
  
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // 'Parent' or 'Student'
  const [widgitEnabled, setWidgitEnabled] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  // Whether the URN gate has been passed this session (URN schools only). Persisted so a
  // refresh on the bare mode-selection URL doesn't loop back to the URN screen.
  const [urnVerified, setUrnVerifiedState] = useState(false);

  // Load school info by slug
  useEffect(() => {
    if (!schoolSlug) {
      setLoading(false);
      return;
    }

    const loadSchool = async () => {
      try {
        setLoading(true);
        setError(null);
        const schoolData = await kioskApi.getSchoolBySlug(schoolSlug);
        setSchool(schoolData);
        
        // Check for existing session
        const savedSession = localStorage.getItem(KIOSK_SESSION_KEY);
        if (savedSession) {
          try {
            const session = JSON.parse(savedSession);
            if (session.schoolSlug === schoolSlug && session.schoolId === schoolData.schoolId) {
              setMode(session.mode);
              setWidgitEnabled(session.widgitEnabled || false);
              setUrnVerifiedState(session.urnVerified || false);
              setSessionData(session);
            }
          } catch (e) {
            console.error('Error parsing saved session:', e);
          }
        }
      } catch (err) {
        setError(err.response?.data || { message: 'Failed to load school information' });
      } finally {
        setLoading(false);
      }
    };

    loadSchool();
  }, [schoolSlug]);

  // Save session to localStorage
  const saveSession = useCallback((modeValue, widgitValue = false) => {
    if (!school) return;
    
    const session = {
      schoolSlug,
      schoolId: school.schoolId,
      mode: modeValue,
      widgitEnabled: widgitValue,
      // Preserve URN verification — otherwise a refresh after entering a mode loses the flag
      // and a URN school is wrongly sent back through the URN gate.
      urnVerified,
      timestamp: Date.now(),
    };

    localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
    setMode(modeValue);
    setWidgitEnabled(widgitValue);
    setSessionData(session);

    // Update backend (public, slug-keyed — the kiosk device has no auth token)
    if (modeValue) {
      kioskApi.setKioskMode(schoolSlug, modeValue).catch(console.error);
    }
  }, [school, schoolSlug, urnVerified]);

  // Clear session
  const clearSession = useCallback(() => {
    localStorage.removeItem(KIOSK_SESSION_KEY);
    setMode(null);
    setWidgitEnabled(false);
    setUrnVerifiedState(false);
    setSessionData(null);
  }, []);

  // Mark the URN gate as passed (called by the URN screen on a valid URN). Persists the flag
  // into the kiosk session so the bare mode-selection URL doesn't re-trigger the URN screen.
  const markUrnVerified = useCallback(() => {
    setUrnVerifiedState(true);
    let session = {};
    try {
      const raw = localStorage.getItem(KIOSK_SESSION_KEY);
      if (raw) session = JSON.parse(raw);
    } catch (e) {
      session = {};
    }
    session = {
      ...session,
      schoolSlug,
      // Keep any existing schoolId if school isn't loaded yet — overwriting with undefined
      // would break the session-restore match and re-trigger the URN gate on refresh.
      schoolId: school?.schoolId ?? session.schoolId,
      urnVerified: true,
      timestamp: Date.now(),
    };
    localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
    setSessionData(session);
  }, [school, schoolSlug]);

  // Reset just the active mode so the bare mode-selection URL shows the picker again
  // (used by the hidden change-mode gesture). URN verification is intentionally preserved
  // so a URN school isn't re-prompted for its URN when staff only want to switch mode.
  const changeMode = useCallback(() => {
    setMode(null);
    setWidgitEnabled(false);
    let session = {};
    try {
      const raw = localStorage.getItem(KIOSK_SESSION_KEY);
      if (raw) session = JSON.parse(raw);
    } catch (e) {
      session = {};
    }
    session = {
      ...session,
      // Always write the identity fields so the persisted session still matches the
      // restore check on refresh (session.schoolSlug === slug && session.schoolId === id) —
      // a missing/corrupt stored session would otherwise drop these and lose urnVerified.
      schoolSlug,
      schoolId: school?.schoolId ?? session.schoolId,
      urnVerified,
      mode: null,
      widgitEnabled: false,
      timestamp: Date.now(),
    };
    localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
    setSessionData(session);
  }, [school, schoolSlug, urnVerified]);

  // Set mode and navigate
  const setKioskMode = useCallback((newMode, widgitValue = false) => {
    saveSession(newMode, widgitValue);
    if (newMode === 'Parent') {
      navigate(paths.kioskParentWelcome.replace(':schoolSlug', schoolSlug));
    } else if (newMode === 'Student') {
      navigate(paths.kioskStudentWelcome.replace(':schoolSlug', schoolSlug));
    }
  }, [saveSession, navigate, schoolSlug]);

  // Exit kiosk mode
  const exitKiosk = useCallback(async () => {
    if (schoolSlug) {
      try {
        await kioskApi.exitKiosk(schoolSlug);
      } catch (err) {
        console.error('Error exiting kiosk:', err);
      }
    }
    clearSession();
    window.location.href = getWelbeeHomeUrl();
  }, [schoolSlug, clearSession]);

  // Toggle Widgit
  const toggleWidgit = useCallback((enabled) => {
    setWidgitEnabled(enabled);
    if (mode && school) {
      const session = {
        ...sessionData,
        widgitEnabled: enabled,
        timestamp: Date.now(),
      };
      localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
      setSessionData(session);
    }
  }, [mode, school, sessionData]);

  const value = {
    school,
    loading,
    error,
    mode,
    widgitEnabled,
    sessionData,
    urnVerified,
    markUrnVerified,
    changeMode,
    setKioskMode,
    exitKiosk,
    clearSession,
    toggleWidgit,
    schoolSlug,
    schoolId: school?.schoolId,
  };

  return <KioskContext.Provider value={value}>{children}</KioskContext.Provider>;
};
